/**
 * A test's setup on the phone — type, test pressure, hold, allowable drop,
 * gauge and recorder (PT-3). Used to ADD a test from the job screen and to
 * change a test's setup before its hold starts.
 *
 * THE PICKERS ARE PT-2'S (`InstrumentPicker`, PtJobDialogs.tsx): every gauge
 * and recorder is listed, and one whose record already rules it out is
 * disabled with the reason in its label — the overdue Test Gauge 0-5K reads
 * "Calibration overdue N days". That is a courtesy; the API's guard decides,
 * and its refusal is drawn under the field it names, as sent.
 *
 * Choosing a type fills hold and allowable drop from the type's defaults where
 * nothing is typed yet. On create, a blank hold or allowable is left OUT of
 * the body so the API applies the type's own default. On edit only the fields
 * the reader changed are sent: an unchanged pressure echoed back from its 0.1
 * rounding is a change to the API, and a changed guarded field re-runs the
 * guard.
 *
 * No hold times and no result here — the hold is stamped by the server and
 * the result is recorded on the test screen.
 */
import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useCreatePtTest, usePtInstruments, usePtTestTypes, useUpdatePtTest } from '../../../hooks/usePressureTesting';
import { pressureFromInput, pressureInputValue } from '../../../lib/pressure-testing';
import type { PtPressureUnit, PtTest, PtTestWrite } from '../../../types/pressure-testing';
import { FieldError, InstrumentPicker, intFromInput, ptWriteError, type PtWriteError } from '../PtJobDialogs';
import { PT_INPUT, PtField } from '../PtBits';
import { PT_FIELD_PRIMARY, PT_FIELD_SECONDARY, PtRefusal } from './PtFieldBits';

interface SetupValues {
  test_type_id: string;
  test_pressure: string;
  hold_minutes: string;
  allowable_drop: string;
  gauge_equipment_id: string;
  recorder_equipment_id: string;
}

function valuesFrom(test: PtTest | null): SetupValues {
  return {
    test_type_id: test?.test_type_id ?? '',
    test_pressure: pressureInputValue(test?.test_pressure),
    hold_minutes: test?.hold_minutes == null ? '' : String(test.hold_minutes),
    allowable_drop: pressureInputValue(test?.allowable_drop),
    gauge_equipment_id: test?.gauge.equipment_id ?? '',
    recorder_equipment_id: test?.recorder?.equipment_id ?? '',
  };
}

const GAUGES = { kind: 'gauge' };
const RECORDERS = { kind: 'recorder' };
const BIG_INPUT = `${PT_INPUT} h-12 text-base`;

export function PtTestSetupForm({
  jobId,
  test,
  unit,
  onSaved,
  onCancel,
}: {
  jobId: string;
  /** Null to add a test. */
  test: PtTest | null;
  unit: PtPressureUnit | null;
  onSaved: (saved: PtTest | null) => void;
  onCancel: () => void;
}) {
  const [initial] = useState<SetupValues>(() => valuesFrom(test));
  const [v, setV] = useState<SetupValues>(initial);
  const [error, setError] = useState<PtWriteError | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const types = usePtTestTypes();
  const gauges = usePtInstruments(GAUGES);
  const recorders = usePtInstruments(RECORDERS);
  const create = useCreatePtTest();
  const update = useUpdatePtTest();

  const typeList = useMemo(() => {
    const list = types.data?.data ?? [];
    // A test's own type stays an option after it is deactivated (the API allows it on this test).
    if (test && !list.some((t) => t.id === test.test_type_id)) {
      return [{ id: test.test_type_id, name: test.test_type_name ?? '—', requires_recorder: test.requires_recorder, default_hold_minutes: null, default_allowable_drop: null }, ...list];
    }
    return list;
  }, [types.data, test]);
  const chosen = typeList.find((t) => t.id === v.test_type_id) ?? null;
  const requiresRecorder = !!chosen?.requires_recorder;
  const pressure = pressureFromInput(v.test_pressure);
  const pressureForPicker = pressure === null || Number.isNaN(pressure) ? null : pressure;
  const pu = unit ?? '';

  const set = (k: keyof SetupValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  const chooseType = (id: string) => {
    const t = typeList.find((x) => x.id === id);
    setV((prev) => ({
      ...prev,
      test_type_id: id,
      hold_minutes: prev.hold_minutes || (t?.default_hold_minutes != null ? String(t.default_hold_minutes) : ''),
      allowable_drop: prev.allowable_drop || pressureInputValue(t?.default_allowable_drop),
    }));
  };

  const errorsFor = (...fields: string[]) => {
    if (!error) return [];
    const listed = error.errors.filter((e) => fields.includes(e.field)).map((e) => e.error);
    if (listed.length) return listed;
    return error.field && fields.includes(error.field) ? [error.message] : [];
  };
  const shownAtField =
    !!error && (error.errors.length > 0 || (!!error.field && ['gauge_equipment_id', 'recorder_equipment_id', 'test_pressure', 'test_type_id'].includes(error.field)));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLocalError(null);
    if (!v.test_type_id) return setLocalError('Choose a test type.');
    if (pressure === null) return setLocalError('Enter the test pressure.');
    if (Number.isNaN(pressure)) return setLocalError('Test pressure must be a number.');
    const hold = intFromInput(v.hold_minutes);
    if (Number.isNaN(hold)) return setLocalError('Hold minutes must be a whole number.');
    const drop = pressureFromInput(v.allowable_drop);
    if (Number.isNaN(drop)) return setLocalError('Allowable drop must be a number.');
    if (!v.gauge_equipment_id) return setLocalError('Choose a gauge.');

    const full: PtTestWrite = {
      test_type_id: v.test_type_id,
      test_pressure: pressure,
      hold_minutes: hold,
      allowable_drop: drop,
      gauge_equipment_id: v.gauge_equipment_id,
      recorder_equipment_id: v.recorder_equipment_id || null,
    };
    const onError = (err: unknown) => setError(ptWriteError(err, 'The test was not saved.'));

    if (!test) {
      const body = { ...full };
      if (hold === null) delete body.hold_minutes;
      if (drop === null) delete body.allowable_drop;
      create.mutate({ jobId, body }, { onSuccess: (res) => onSaved(res.data), onError });
      return;
    }
    const body: PtTestWrite = {};
    (Object.keys(initial) as (keyof SetupValues)[]).forEach((k) => {
      if (v[k] !== initial[k]) (body as Record<string, unknown>)[k] = (full as Record<string, unknown>)[k];
    });
    if (!Object.keys(body).length) return onSaved(test);
    update.mutate({ id: test.id, body }, { onSuccess: (res) => onSaved(res.data), onError });
  };

  const pending = create.isPending || update.isPending;

  return (
    <form onSubmit={submit} className="space-y-4" noValidate data-testid="pt-setup-form">
      <PtRefusal message={localError} testId="pt-setup-local-error" />
      {error && !shownAtField && <PtRefusal message={error.message} testId="pt-setup-error" />}

      <div>
        <PtField label="Test type" hint={types.isError ? 'The test types could not be loaded.' : undefined}>
          <select className={BIG_INPUT} value={v.test_type_id} onChange={(e) => chooseType(e.target.value)} data-testid="pt-setup-type">
            <option value="">{types.isLoading ? 'Loading…' : 'Choose a test type'}</option>
            {typeList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.requires_recorder ? ' (chart recorder)' : ''}
              </option>
            ))}
          </select>
        </PtField>
        <FieldError messages={errorsFor('test_type_id')} />
      </div>

      <div>
        <PtField label={`Test pressure${pu ? ` (${pu})` : ''}`}>
          <input className={BIG_INPUT} inputMode="decimal" value={v.test_pressure} onChange={set('test_pressure')} data-testid="pt-setup-pressure" />
        </PtField>
        <FieldError messages={errorsFor('test_pressure')} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PtField label="Hold (min)" hint={!test && chosen ? 'Blank uses the type default.' : undefined}>
          <input className={BIG_INPUT} inputMode="numeric" value={v.hold_minutes} onChange={set('hold_minutes')} data-testid="pt-setup-hold" />
        </PtField>
        <PtField label={`Allowable drop${pu ? ` (${pu})` : ''}`}>
          <input className={BIG_INPUT} inputMode="decimal" value={v.allowable_drop} onChange={set('allowable_drop')} data-testid="pt-setup-drop" />
        </PtField>
      </div>

      <InstrumentPicker
        label="Gauge"
        kind="gauge"
        value={v.gauge_equipment_id}
        onChange={(id) => setV((prev) => ({ ...prev, gauge_equipment_id: id }))}
        instruments={gauges.data?.data ?? []}
        loading={gauges.isLoading}
        failed={gauges.isError}
        testPressure={pressureForPicker}
        unit={unit}
        current={test ? test.gauge : null}
        optional={false}
        required={false}
        errors={errorsFor('gauge_equipment_id')}
        testId="pt-gauge"
      />
      <InstrumentPicker
        label="Chart recorder"
        kind="recorder"
        value={v.recorder_equipment_id}
        onChange={(id) => setV((prev) => ({ ...prev, recorder_equipment_id: id }))}
        instruments={recorders.data?.data ?? []}
        loading={recorders.isLoading}
        failed={recorders.isError}
        testPressure={pressureForPicker}
        unit={unit}
        current={test?.recorder ?? null}
        optional={!requiresRecorder}
        required={requiresRecorder}
        errors={errorsFor('recorder_equipment_id')}
        testId="pt-recorder"
      />

      <div className="grid grid-cols-2 gap-3 pt-1">
        <button type="button" className={PT_FIELD_SECONDARY} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className={PT_FIELD_PRIMARY} disabled={pending} data-testid="pt-setup-save">
          {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          {test ? 'Save setup' : 'Add test'}
        </button>
      </div>
    </form>
  );
}
